import { useState } from "react";
import Header from "@/components/Header";
import Contact from "@/components/Contact";
import InlineContactForm from "@/components/InlineContactForm";
import Footer from "@/components/Footer";
import MeetingSchedulingForm from "@/components/MeetingSchedulingForm";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import SkylineHero from "@/components/SkylineHero";

const ContactPage = () => {
  const [isMeetingFormOpen, setIsMeetingFormOpen] = useState(false);
  const contactAnimation = useScrollAnimation();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Back button */}
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 z-20">
        <a 
          href="/"
          className="flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-3 py-2 border border-border/50"
        >
          <svg className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 19-7-7 7-7"/>
            <path d="M19 12H5"/>
          </svg>
          <span className="text-xs sm:text-sm">Back to Home</span>
        </a>
      </div>
      
      {/* Hero Section */}
      <section className="min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden pt-16 sm:pt-20">
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-[0.9] tracking-tight text-foreground">
                  Get In Touch
                </h1>
                <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-foreground max-w-3xl mx-auto leading-relaxed">
                Connect with our team to explore investment opportunities, partnerships, and innovative solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section ref={contactAnimation.ref} className={`scroll-fade-in ${contactAnimation.isVisible ? 'visible' : 'opacity-100'}`}>
        <div className="w-full relative">
          {/* Contact Cards Section */}
          <section id="contact" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-background">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12 md:mb-16">
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-black mb-3 sm:mb-4 md:mb-6">
                    Get In Touch
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-charcoal max-w-3xl mx-auto leading-relaxed">
                    Ready to explore new opportunities? Connect with our specialized teams for investments, partnerships, and innovation.
                  </p>
                </div>

                {/* Contact Types - from Contact component */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
                  <div className="p-4 sm:p-6 md:p-8 text-center hover:shadow-card transition-all duration-300 flex flex-col bg-card rounded-lg border">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <svg className="text-white w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-black mb-3 sm:mb-4">
                      Investment Opportunities
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-charcoal mb-4 sm:mb-6 leading-relaxed flex-1">
                      Explore investment opportunities in our diverse portfolio of developments and hospitality projects.
                    </p>
                    <div className="mb-4 sm:mb-6">
                      <a href="mailto:investments@brassbellhospitality.com" className="text-xs sm:text-sm md:text-base text-charcoal hover:text-black transition-colors font-medium">
                        investments@brassbellhospitality.com
                      </a>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6 md:p-8 text-center hover:shadow-card transition-all duration-300 flex flex-col bg-card rounded-lg border">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <svg className="text-white w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-black mb-3 sm:mb-4">
                      Business Development
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-charcoal mb-4 sm:mb-6 leading-relaxed flex-1">
                      Partner with us for strategic collaborations and business development opportunities.
                    </p>
                    <div className="mb-4 sm:mb-6">
                      <a href="mailto:business@brassbellhospitality.com" className="text-xs sm:text-sm md:text-base text-charcoal hover:text-black transition-colors font-medium">
                        business@brassbellhospitality.com
                      </a>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6 md:p-8 text-center hover:shadow-card transition-all duration-300 flex flex-col bg-card rounded-lg border sm:col-span-2 lg:col-span-1">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <svg className="text-white w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0H8m8 0v2a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0H8" />
                      </svg>
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-bold text-black mb-3 sm:mb-4">
                      Technology Solutions
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-charcoal mb-4 sm:mb-6 leading-relaxed flex-1">
                      Discover our innovative technology solutions and digital transformation services.
                    </p>
                    <div className="mb-4 sm:mb-6">
                      <a href="mailto:technology@brassbellhospitality.com" className="text-xs sm:text-sm md:text-base text-charcoal hover:text-black transition-colors font-medium">
                        technology@brassbellhospitality.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form Section */}
          <section className="bg-background/50">
            <InlineContactForm />
          </section>

          {/* Ready to Work Together CTA Section */}
          <section className="py-12 sm:py-16 md:py-20 bg-background">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="max-w-6xl mx-auto">
                <div className="text-center">
                  <div className="bg-gradient-to-r from-black to-black/90 p-6 sm:p-8 md:p-12 rounded-2xl text-white">
                    <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">
                      Ready to Work Together?
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto">
                      Let's schedule a meeting to discuss your project and explore how we can help you achieve your goals.
                    </p>
                    <div className="flex justify-center">
                      <Button 
                        variant="secondary" 
                        size="lg"
                        onClick={() => setIsMeetingFormOpen(true)}
                        className="text-sm sm:text-base"
                      >
                        Schedule a Meeting
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>

      <MeetingSchedulingForm 
        open={isMeetingFormOpen} 
        onOpenChange={setIsMeetingFormOpen} 
      />
      
      <Footer />
    </div>
  );
};

export default ContactPage;